import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { RevoltModal, RevoltButton, RevoltInput, RevoltColorPicker, RevoltDropdownButton } from '../../../../../components';
import './RecommendedModelDownloadModal.css';

function hsvToRgbHex(h, s, v) {
  const c = v * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = v - c;

  let r1 = 0;
  let g1 = 0;
  let b1 = 0;

  if (h >= 0 && h < 60) {
    r1 = c; g1 = x; b1 = 0;
  } else if (h >= 60 && h < 120) {
    r1 = x; g1 = c; b1 = 0;
  } else if (h >= 120 && h < 180) {
    r1 = 0; g1 = c; b1 = x;
  } else if (h >= 180 && h < 240) {
    r1 = 0; g1 = x; b1 = c;
  } else if (h >= 240 && h < 300) {
    r1 = x; g1 = 0; b1 = c;
  } else {
    r1 = c; g1 = 0; b1 = x;
  }

  const r = Math.round((r1 + m) * 255);
  const g = Math.round((g1 + m) * 255);
  const b = Math.round((b1 + m) * 255);

  const toHex = (n) => n.toString(16).padStart(2, '0').toUpperCase();
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

function pickRandomModelColor() {
  // Saturated + bright: matches the “HSL: saturation 100%, brightness 100%” vibe.
  const hue = Math.random() * 360;
  return hsvToRgbHex(hue, 1, 1);
}

function formatBytes(bytes) {
  if (!Number.isFinite(bytes) || bytes < 0) return '—';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let i = 0;
  let v = bytes;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  const digits = i === 0 ? 0 : i === 1 ? 1 : 2;
  return `${v.toFixed(digits)} ${units[i]}`;
}

function formatEtaSeconds(etaSeconds) {
  if (!Number.isFinite(etaSeconds) || etaSeconds < 0) return '—';
  const s = Math.floor(etaSeconds);
  const hh = Math.floor(s / 3600);
  const mm = Math.floor((s % 3600) / 60);
  const ss = s % 60;
  if (hh > 0) return `${hh}:${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
  return `${mm}:${String(ss).padStart(2, '0')}`;
}

export default function RecommendedModelDownloadModal({
  isOpen,
  onClose,
  recommendedModel,
  onViewModel,
  /**
   * Which IPC to use for the download:
   *  - 'recommended' → window.modelsAPI.downloadAndImportRecommended (default)
   *  - 'url'         → window.modelsAPI.downloadAndImportUrl (any GGUF URL,
   *                    used by the Hugging Face Discover flow)
   */
  downloadSource = 'recommended',
  titleLabel = 'Download Recommended Model',
}) {
  const [status, setStatus] = useState('idle'); // idle | downloading | complete | error
  const [error, setError] = useState(null);

  const activeDownloadIdRef = useRef(null);
  const completionAtRef = useRef(null);
  const [progress, setProgress] = useState({
    percent: 0,
    transferredBytes: 0,
    totalBytes: null,
    bytesPerSecond: null,
    etaSeconds: null,
  });

  const unSubRef = useRef(null);

  const [importedModel, setImportedModel] = useState(null);
  const [viewModelReady, setViewModelReady] = useState(false);
  const [downloadStarted, setDownloadStarted] = useState(false);
  const [modelColor, setModelColor] = useState(null);

  const modeLabel = useMemo(() => {
    // reserved for future integration; current recommended models are always URL mode
    return recommendedModel?.mode || 'url';
  }, [recommendedModel]);

  useEffect(() => {
    if (!isOpen) return;

    setStatus('idle');
    setError(null);
    activeDownloadIdRef.current = null;
    completionAtRef.current = null;
    setImportedModel(null);
    setViewModelReady(false);
    setDownloadStarted(false);
    setModelColor(pickRandomModelColor());
    setProgress({
      percent: 0,
      transferredBytes: 0,
      totalBytes: null,
      bytesPerSecond: null,
      etaSeconds: null,
    });

    return () => {
      if (unSubRef.current) {
        try { unSubRef.current(); } catch (_) { /* ignore */ }
        unSubRef.current = null;
      }
    };
  }, [isOpen]);

  const subscribeToProgress = useCallback(() => {
    if (typeof window === 'undefined' || !window.modelsAPI?.onDownloadProgress) return;
    if (unSubRef.current) return;

    unSubRef.current = window.modelsAPI.onDownloadProgress((payload) => {
      if (!payload) return;

      // First payload determines the active downloadId.
      if (!activeDownloadIdRef.current && payload.downloadId) {
        activeDownloadIdRef.current = payload.downloadId;
      }

      if (activeDownloadIdRef.current && payload.downloadId && payload.downloadId !== activeDownloadIdRef.current) {
        return;
      }

      if (payload.phase === 'starting') {
        setDownloadStarted(true);
        setStatus('downloading');
        return;
      }

      if (payload.phase === 'downloading') {
        setStatus('downloading');
        setProgress({
          percent: typeof payload.percent === 'number' ? payload.percent : 0,
          transferredBytes: payload.transferredBytes ?? 0,
          totalBytes: payload.totalBytes ?? null,
          bytesPerSecond: payload.bytesPerSecond ?? null,
          etaSeconds: payload.etaSeconds ?? null,
        });
      }

      if (payload.phase === 'complete') {
        setStatus('complete');
        completionAtRef.current = Date.now();
        setProgress({
          percent: typeof payload.percent === 'number' ? payload.percent : 100,
          transferredBytes: payload.transferredBytes ?? 0,
          totalBytes: payload.totalBytes ?? null,
          bytesPerSecond: payload.bytesPerSecond ?? null,
          etaSeconds: payload.etaSeconds ?? 0,
        });
      }
    });
  }, []);

  const handleDownload = useCallback(async () => {
    if (!recommendedModel) return;
    if (status === 'downloading') return;

    setError(null);
    setStatus('idle');
    setImportedModel(null);
    setViewModelReady(false);
    completionAtRef.current = null;
    setDownloadStarted(false);

    try {
      if (!window?.dialogAPI?.showOpenDialog) {
        throw new Error('Folder selection is only available in the desktop app');
      }

      const result = await window.dialogAPI.showOpenDialog({
        title: 'Select folder to save the model',
        properties: ['openDirectory'],
      });

      if (result?.canceled) return;
      if (!result?.filePaths || result.filePaths.length === 0) return;

      const destinationFolder = result.filePaths[0];

      // Subscribe after folder selection so cancel doesn't leave an active listener.
      subscribeToProgress();
      setStatus('downloading');

      const ipcCall =
        downloadSource === 'url'
          ? window.modelsAPI.downloadAndImportUrl
          : window.modelsAPI.downloadAndImportRecommended;
      if (typeof ipcCall !== 'function') {
        throw new Error('Download API not available');
      }
      const downloadResult = await ipcCall(
        recommendedModel,
        destinationFolder,
        modelColor
      );

      // Keep UI at 100% for at least 500ms before enabling the next step.
      const completedAt = completionAtRef.current;
      const elapsedMs = completedAt ? Date.now() - completedAt : 0;
      const waitMs = completedAt ? Math.max(0, 500 - elapsedMs) : 500;
      if (waitMs > 0) {
        await new Promise((r) => setTimeout(r, waitMs));
      }

      setImportedModel(downloadResult?.importedModel || null);
      setViewModelReady(true);
    } catch (e) {
      setStatus('error');
      setError(e?.message || 'Failed to download model');
    } finally {
      if (unSubRef.current) {
        try { unSubRef.current(); } catch (_) { /* ignore */ }
        unSubRef.current = null;
      }
    }
  }, [recommendedModel, status, subscribeToProgress, modelColor, downloadSource]);

  const handleViewModel = useCallback(() => {
    if (!importedModel) return;
    onClose?.();
    onViewModel?.(importedModel);
  }, [importedModel, onClose, onViewModel]);

  if (!isOpen) return null;

  const fileSizeText =
    typeof recommendedModel?.fileSize === 'string' && recommendedModel?.fileSize.trim().length > 0
      ? recommendedModel.fileSize
      : '—';

  return (
    <RevoltModal
      isOpen={isOpen}
      onClose={status === 'downloading' ? undefined : onClose}
      title={
        <div className="recommended-model-modal-title">
          <i className="fa-solid fa-download" />
          {titleLabel}
        </div>
      }
      closeOnBackdrop={status !== 'downloading'}
      showCloseButton={true}
      containerClassName="recommended-model-modal-container"
    >
      <div className="recommended-model-modal-body">
        <div className="recommended-model-info">
          <div className="recommended-model-field">
            <label>Model Name</label>
            <div className="recommended-model-name-row">
              <div className="recommended-model-name-input">
                <RevoltInput value={recommendedModel?.displayName || ''} disabled />
              </div>
              <div className="recommended-model-name-color">
                <RevoltColorPicker
                  value={modelColor}
                  onChange={(color) => setModelColor(color)}
                />
              </div>
            </div>
          </div>

          <div className="recommended-model-field">
            <label>Download URL</label>
            <RevoltInput value={recommendedModel?.downloadUrl || ''} disabled />
          </div>

          <div className="recommended-model-meta-grid">
            <div className="recommended-model-meta-item">
              <span className="label">File Size</span>
              <span className="value">{fileSizeText}</span>
            </div>
            <div className="recommended-model-meta-item">
              <span className="label">Quantization</span>
              <span className="value">{recommendedModel?.quantization || '—'}</span>
            </div>
            <div className="recommended-model-meta-item">
              <span className="label">Mode</span>
              <span className="value">{modeLabel}</span>
            </div>
          </div>

          <div className="recommended-model-description">
            <div className="label">Description</div>
            <div className="value">{recommendedModel?.description || '—'}</div>
          </div>
        </div>

        {(downloadStarted || status === 'complete' || status === 'error') && (
          <div className="recommended-model-progress-section">
            <div className="recommended-model-progress-header">
              <h4>Download Progress</h4>
              {status === 'downloading' && (
                <span className="recommended-model-progress-subtext">Estimating…</span>
              )}
              {status === 'complete' && (
                <span className="recommended-model-progress-subtext">Complete</span>
              )}
              {status === 'error' && (
                <span className="recommended-model-progress-subtext recommended-model-progress-subtext--error">
                  Error
                </span>
              )}
            </div>

            <div className="recommended-model-progress-bar">
              <div
                className="recommended-model-progress-fill"
                style={{ width: `${Math.max(0, Math.min(100, progress.percent || 0))}%` }}
              />
            </div>

            <div className="recommended-model-progress-stats">
              <span>
                {progress.totalBytes
                  ? `${formatBytes(progress.transferredBytes)} / ${formatBytes(progress.totalBytes)}`
                  : formatBytes(progress.transferredBytes)}
              </span>
              <span>
                {progress.bytesPerSecond
                  ? `${(progress.bytesPerSecond / 1024 / 1024).toFixed(2)} MB/s`
                  : '—'}
              </span>
              <span>ETA: {formatEtaSeconds(progress.etaSeconds)}</span>
            </div>

            {error && <div className="recommended-model-error">{error}</div>}
          </div>
        )}

        <div className="recommended-model-actions">
          {status === 'complete' ? (
            <>
              <RevoltDropdownButton
                title="Open this model in My Models"
                showDropdown={false}
                onClick={handleViewModel}
                disabled={!viewModelReady || !importedModel}
                className="recommended-model-toolbar-btn"
              >
                <i className="fa-solid fa-eye" />
                View Model
              </RevoltDropdownButton>
              <RevoltButton variant="ghost" onClick={onClose}>
                Close
              </RevoltButton>
            </>
          ) : (
            <>
              <RevoltDropdownButton
                title="Choose a folder, download, and import"
                showDropdown={false}
                onClick={handleDownload}
                disabled={status === 'downloading' || !recommendedModel?.downloadUrl}
                className="recommended-model-toolbar-btn"
              >
                <i className="fa-solid fa-download" />
                {status === 'downloading' ? 'Downloading…' : 'Download'}
              </RevoltDropdownButton>
              <RevoltButton variant="ghost" onClick={onClose} disabled={status === 'downloading'}>
                Cancel
              </RevoltButton>
            </>
          )}
        </div>
      </div>
    </RevoltModal>
  );
}

